import { Fragment, useEffect, useMemo } from 'react';

import PortalEntry from '@/components/common/PortalEntry';
import { WalletTypes } from '@/constants/general';
import { FlattenPortalRequest, RequestWorkflowResponse } from '@/types/sso';
import { getCurrency } from '@/utils/hooks/useCurrency';
import useCurrencyOptions from '@/utils/hooks/useCurrencyOptions';
import useLanguage from '@/utils/hooks/useLanguage';
import { useSso } from '@/utils/providers/SSOProvider';

interface WalletDetailsProps {
    selectedWallet?: FlattenPortalRequest;
    workflowsData?: RequestWorkflowResponse[];
}

function formatType(str) {
    return str
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

const WalletDetails = {
    Section1: (props: WalletDetailsProps) => {
        const { selectedWallet, workflowsData } = props;
        const lang = useLanguage();
        const { vaspApi, blockchainsApi } = useSso();
        const { currenciesData } = useCurrencyOptions();

        const { data: vaspData } = vaspApi;
        const { data: blockchainsData } = blockchainsApi;

        const networkName = useMemo(() => {
            return blockchainsData?.find((b) => b.id === selectedWallet?.networkId)?.name || '-';
        }, [selectedWallet, blockchainsData]);

        const selectedVasp = useMemo(() => {
            return vaspData?.find((vasp) => vasp.id === selectedWallet?.vaspId || vasp.id === selectedWallet?.vasp?.id);
        }, [selectedWallet, vaspData]);

        const type = useMemo(() => {
            if (selectedWallet?.requestStatus === 'INITIATED') {
                return formatType(selectedWallet?.type);
            }
            return selectedWallet?.type;
        }, [selectedWallet]);

        const assets = useMemo(() => {
            if (selectedWallet?.supportedAssets) {
                return selectedWallet.supportedAssets
                    .map((asset) => {
                        const { show } = getCurrency(asset.digitalAssetName || asset.currencyName || '');
                        return show;
                    })
                    .join(', ');
            }
            if (selectedWallet?.assetIds) {
                return selectedWallet.assetIds
                    .map((assetId) => {
                        const asset = currenciesData?.find((c) => c.id === assetId);
                        const { show } = getCurrency(asset?.digitalAssetName || asset?.name || '');
                        return show;
                    })
                    .join(', ');
            }
            return '-';
        }, [selectedWallet]);

        useEffect(() => {
            vaspApi.mutate();
            blockchainsApi.mutate();
        }, []);

        return (
            <Fragment>
                <fieldset className="space-y-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.walletInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry value={selectedWallet?.friendlyName} label={lang.portal.friendlyName} />
                        </div>
                        <div className="flex-1">
                            <PortalEntry label="Wallet Type" value={type} />
                        </div>
                    </div>
                </fieldset>

                {type === WalletTypes.Regulated && (
                    <fieldset className="space-y-3 mt-3">
                        <legend className="text-xs text-brand-neutral-500">{lang.portal.vaspInformation}</legend>
                        <div className="flex flex-col xsm:flex-row gap-4 w-full">
                            <div className="flex-1">
                                <PortalEntry value={selectedVasp?.name} label={lang.portal.vasp} />
                            </div>
                            {selectedVasp?.legalEntityName && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspLEN}
                                        value={selectedVasp?.legalEntityName}
                                        hidden={!selectedVasp?.legalEntityName}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col xsm:flex-row gap-4 w-full">
                            {selectedVasp?.country && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspCountry}
                                        value={selectedVasp?.country || '-'}
                                        hidden={!selectedVasp?.country}
                                    />
                                </div>
                            )}
                            {selectedVasp?.lei && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspLEI}
                                        value={selectedVasp?.lei || '-'}
                                        hidden={!selectedVasp?.lei}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col xsm:flex-row gap-4 w-full">
                            {selectedVasp?.emailAddress && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspEmail}
                                        value={selectedVasp?.emailAddress || '-'}
                                        hidden={!selectedVasp?.emailAddress}
                                    />
                                </div>
                            )}
                            {selectedVasp?.address && (
                                <div className="flex-1">
                                    <PortalEntry
                                        label={lang.portal.vaspAddress}
                                        value={selectedVasp?.address || '-'}
                                        hidden={!selectedVasp?.address}
                                    />
                                </div>
                            )}
                        </div>
                    </fieldset>
                )}

                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.networkInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedWallet?.networkName || networkName}
                                label={lang.portal.network}
                            />
                        </div>
                        <div className="flex-1">
                            {selectedWallet?.supportedAssets && (
                                <PortalEntry value={assets} label={lang.portal.assets} />
                            )}
                            {selectedWallet?.assetIds && <PortalEntry value={assets} label={lang.portal.assets} />}
                        </div>
                    </div>
                </fieldset>

                <fieldset className="space-y-3">
                    <PortalEntry value={selectedWallet?.walletAddress} label={lang.portal.walletAddress} />
                    <PortalEntry value={selectedWallet?.tagMemo} label={lang.portal.tagMemo} />
                </fieldset>
                {workflowsData && workflowsData.length > 1 && workflowsData[1]?.remark && (
                    <PortalEntry value={workflowsData[1].remark} label={lang.portal.remark} />
                )}
            </Fragment>
        );
    },
    Section2: (props: WalletDetailsProps) => {
        const { selectedWallet, workflowsData } = props;

        const lang = useLanguage();
        // const dateFormatter = useDateFormatter({
        //     hour12: false,
        //     timeZone: 'UTC',
        //     ...tzOptions
        // });

        return (
            <fieldset className="space-y-3">
                <legend className="text-xs text-brand-neutral-500">{lang.portal.additionalInformation}</legend>
                <div className="flex flex-col xsm:flex-row gap-4 w-full">
                    <div className="flex-1">
                        <PortalEntry
                            value={workflowsData?.find((workflow) => workflow.remark)?.remark || ''}
                            label={lang.portal.remark}
                        />
                    </div>
                </div>
                {/* <Timeline.Main>
                    {workflowsData &&
                        workflowsData.map((workflow, index) => {
                            return (
                                <Timeline.Entry
                                    key={index}
                                    isLast={workflowsData.length === index + 1 && workflowsData.length !== 1}
                                    status={getStatusFromRequestStatus(workflow.requestStatus)}>
                                    <Timeline.Title>{workflow.requestStatus}</Timeline.Title>
                                    <Timeline.Time>{dateFormatter.format(new Date(workflow.updatedAt))}</Timeline.Time>
                                    {workflow.remark && (
                                        <Timeline.Description className="text-sm my-2 mt-3 text-neutral-200 border border-neutral-500 p-2">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-0.5">Remark</div>
                                                <span className="text-xs text-justify">{workflow.remark}</span>
                                            </div>
                                        </Timeline.Description>
                                    )}
                                    <Timeline.Description className="text-xs mt-1 text-neutral-400">
                                        Action performed by - {workflow.actorName}
                                    </Timeline.Description>
                                </Timeline.Entry>
                            );
                        })}
                </Timeline.Main> */}
            </fieldset>
        );
    },
    Summary: (props: WalletDetailsProps) => {
        const { selectedWallet } = props;

        const lang = useLanguage();
        const { blockchainsApi } = useSso();
        const { currenciesData } = useCurrencyOptions();
        const { data: blockchainsData } = blockchainsApi;

        const networkName = useMemo(() => {
            return blockchainsData?.find((b) => b.id === selectedWallet?.networkId)?.name || '-';
        }, [selectedWallet, blockchainsData]);

        const assets = useMemo(() => {
            if (selectedWallet?.supportedAssets) {
                return selectedWallet.supportedAssets
                    .map((asset) => {
                        const { show } = getCurrency(asset.digitalAssetName || asset.currencyName || '');
                        return show;
                    })
                    .join(', ');
            }
            if (selectedWallet?.assetIds) {
                return selectedWallet.assetIds
                    .map((assetId) => {
                        const asset = currenciesData?.find((c) => c.id === assetId);
                        const { show } = getCurrency(asset?.digitalAssetName || asset?.name || '');
                        return show;
                    })
                    .join(', ');
            }
            return '-';
        }, [selectedWallet]);

        return (
            <Fragment>
                <fieldset className="space-y-3 mt-3">
                    <legend className="text-xs text-brand-neutral-500">{lang.portal.networkInformation}</legend>
                    <div className="flex flex-col xsm:flex-row gap-4 w-full">
                        <div className="flex-1">
                            <PortalEntry
                                value={selectedWallet?.networkName || networkName}
                                label={lang.portal.network}
                            />
                        </div>
                        <div className="flex-1">
                            <PortalEntry value={assets} label={lang.portal.assets} />
                        </div>
                    </div>
                </fieldset>

                <fieldset className="space-y-3">
                    <PortalEntry value={selectedWallet?.walletAddress} label={lang.portal.walletAddress} />
                    <PortalEntry value={selectedWallet?.tagMemo} label={lang.portal.tagMemo} />
                </fieldset>
            </Fragment>
        );
    }
};

export default WalletDetails;

const getStatusFromRequestStatus = (requestStatus: string) => {
    switch (requestStatus) {
        case 'INITIATED':
            return 'success';
        case 'APPROVED':
            return 'success';
        case 'REJECTED':
            return 'error';
        case 'CANCELED':
            return 'error';
        default:
            return 'warning';
    }
};
